import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {forkJoin, of, Subject} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {CropYieldForecast} from '@6grain/masofa-monolith-api-client/ai-inference';
import {Season, SeasonService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {Crop} from '@6grain/masofa-monolith-api-client/dictionaries';
import {CropService} from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import {Variety} from '@6grain/masofa-monolith-api-client/dictionaries/model/variety';
import {VarietyService} from '@6grain/masofa-monolith-api-client/dictionaries/api/variety.service';
import {ServerTranslateService} from '@app/services/server-translate.service';
import {FieldDetailsDialogComponent} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/field-details-dialog/field-details-dialog.component';
import {
  FieldDetailsView,
  YIELD_FORECAST_TABS,
} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/field-details-dialog/field-details-view';
import {
  buildYieldForecastGeneral,
  YieldForecastGeneralModel,
} from './yield-forecast-general.util';

interface ForecastBundle {
  season: Season | null;
  variety: Variety | null;
  crop: Crop | null;
}

const EMPTY_BUNDLE: ForecastBundle = {season: null, variety: null, crop: null};

@Component({
  selector: 'app-crop-yield-forecast-detail-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FieldDetailsDialogComponent,
  ],
  templateUrl: './crop-yield-forecast-detail-dialog.component.html',
  styleUrl: './crop-yield-forecast-detail-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CropYieldForecastDetailDialogComponent {
  private readonly seasonService = inject(SeasonService);
  private readonly varietyService = inject(VarietyService);
  private readonly cropService = inject(CropService);
  private readonly serverTranslate = inject(ServerTranslateService);
  private readonly translate = inject(TranslateService);
  private readonly requests$ = new Subject<CropYieldForecast | null>();
  private loadedKey = '';

  readonly visible = input(false);
  readonly forecast = input<CropYieldForecast | null>(null);
  readonly fieldName = input('');
  readonly crop = input<Crop | null>(null);
  readonly closed = output<void>();

  readonly loading = signal(false);
  private readonly bundle = signal<ForecastBundle>(EMPTY_BUNDLE);
  private readonly lang = signal(this.translate.currentLang || 'ru-RU');

  readonly general = computed(() => this.buildGeneral());
  readonly fieldView = computed(() => this.toFieldView());
  readonly tabCrops = computed(() => {
    const crop = this.crop() ?? this.bundle().crop;
    return crop?.id ? [crop] : [];
  });
  private readonly details = viewChild(FieldDetailsDialogComponent);
  private openedKey = '';

  constructor() {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe((event) => this.lang.set(event.lang));

    this.requests$
      .pipe(
        switchMap((forecast) => this.fetch(forecast)),
        takeUntilDestroyed(),
      )
      .subscribe((bundle) => {
        this.bundle.set(bundle);
        this.loading.set(false);
      });

    effect(() => {
      const key = this.openKey();
      if (key === this.loadedKey) {
        return;
      }
      this.loadedKey = key;
      untracked(() => this.startLoad());
    });

    effect(() => {
      const key = this.openKey();
      const details = this.details();
      untracked(() => this.syncDialog(key, details));
    });
  }

  onVisibleChange(open: boolean): void {
    if (!open) {
      this.closed.emit();
    }
  }

  onInnerClose(): void {
    this.onVisibleChange(false);
  }

  private openKey(): string {
    if (!this.visible()) {
      return '';
    }
    const forecast = this.forecast();
    return [forecast?.id, forecast?.seasonId, forecast?.fieldId, forecast?.forecastDate].join('|');
  }

  private startLoad(): void {
    const forecast = this.visible() ? this.forecast() : null;
    this.bundle.set(EMPTY_BUNDLE);
    this.loading.set(!!forecast?.seasonId);
    this.requests$.next(forecast);
  }

  private fetch(forecast: CropYieldForecast | null) {
    if (!forecast?.seasonId || !this.visible()) {
      return of(EMPTY_BUNDLE);
    }
    return this.seasonService.cropMonitoringSeasonGetByIdIdGet(forecast.seasonId).pipe(
      switchMap((season) => {
        const variety$ = season?.varietyId
          ? this.varietyService.dictionariesVarietyGetByIdIdGet(season.varietyId).pipe(catchError(() => of(null)))
          : of(null);
        const crop$ = this.crop() || !forecast.cropId
          ? of(this.crop())
          : this.cropService.dictionariesCropGetByIdIdGet(forecast.cropId).pipe(catchError(() => of(null)));
        return forkJoin({season: of(season), variety: variety$, crop: crop$});
      }),
      map(({season, variety, crop}) => ({
        season,
        variety,
        crop,
      })),
      catchError(() => of(EMPTY_BUNDLE)),
    );
  }

  private syncDialog(key: string, details: FieldDetailsDialogComponent | undefined): void {
    if (!key) {
      this.openedKey = '';
      details?.dismissQuiet();
      return;
    }
    if (!details || key === this.openedKey) {
      return;
    }
    this.openedKey = key;
    const fieldId = this.forecast()?.fieldId || null;
    details.show(fieldId ? {id: fieldId} : {}, {seasonId: this.forecast()?.seasonId});
  }

  private toFieldView(): FieldDetailsView {
    const general = this.general();
    return {
      id: 'yield-forecast',
      tabs: YIELD_FORECAST_TABS,
      startTab: 'indices',
      showMore: true,
      dismissableMask: true,
      indicesShowReport: false,
      atmShowAdd: false,
      anomaliesOpenPassport: true,
      loading: this.loading(),
      header: {title: general.title},
      general,
      crops: this.tabCrops(),
    };
  }

  private buildGeneral(): YieldForecastGeneralModel {
    this.lang();
    const bundle = this.bundle();
    const crop = this.crop() ?? bundle.crop;
    return buildYieldForecastGeneral({
      forecast: this.forecast(),
      season: bundle.season,
      cropName: this.serverTranslate.getDisplayValue(crop, 'names'),
      varietyName: this.serverTranslate.getDisplayValue(bundle.variety, 'names'),
      fieldName: this.fieldName(),
    });
  }
}

